import React, { useState, useRef, useEffect } from 'react';
import toast from 'react-hot-toast';
import { email, contractors, buildings } from '../services/api';

const RECORDING_TIPS = [
  'State the building address and unit number if relevant',
  'Describe the issue clearly and what work is needed',
  'Mention any urgency or safety concerns',
  'Include any contractor preference or quote request',
];

export default function VoiceEmail() {
  const [isRecording, setIsRecording] = useState(false);
  const [transcript, setTranscript] = useState('');
  const [composedEmail, setComposedEmail] = useState(null);
  const [composing, setComposing] = useState(false);
  const [sending, setSending] = useState(false);
  const [contractorList, setContractorList] = useState([]);
  const [buildingList, setBuildingList] = useState([]);
  const [selectedContractor, setSelectedContractor] = useState('');
  const [selectedBuilding, setSelectedBuilding] = useState('');
  const [emailForm, setEmailForm] = useState({ to: '', cc: '', subject: '', body: '' });
  const [browserSupported, setBrowserSupported] = useState(true);

  const recognitionRef = useRef(null);
  const interimRef = useRef('');

  useEffect(() => {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRecognition) {
      setBrowserSupported(false);
      return;
    }
    contractors.list().then((r) => setContractorList(r.data)).catch(() => {});
    buildings.list().then((r) => setBuildingList(r.data)).catch(() => {});
  }, []);

  const startRecording = () => {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    const recognition = new SpeechRecognition();
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.lang = 'en-CA';

    let finalTranscript = transcript;

    recognition.onresult = (event) => {
      let interim = '';
      for (let i = event.resultIndex; i < event.results.length; i++) {
        if (event.results[i].isFinal) {
          finalTranscript += event.results[i][0].transcript + ' ';
        } else {
          interim += event.results[i][0].transcript;
        }
      }
      interimRef.current = interim;
      setTranscript(finalTranscript + interim);
    };

    recognition.onerror = (e) => {
      if (e.error !== 'aborted') toast.error(`Microphone error: ${e.error}`);
      setIsRecording(false);
    };

    recognition.onend = () => {
      setTranscript(finalTranscript);
      setIsRecording(false);
    };

    recognitionRef.current = recognition;
    recognition.start();
    setIsRecording(true);
  };

  const stopRecording = () => {
    recognitionRef.current?.stop();
    setIsRecording(false);
  };

  const handleCompose = async () => {
    if (!transcript.trim()) return toast.error('Please record or type a message first');
    setComposing(true);
    try {
      const res = await email.compose({
        transcript: transcript.trim(),
        contractor_id: selectedContractor || null,
        building_id: selectedBuilding || null,
      });
      setComposedEmail(res.data);
      setEmailForm({
        to: res.data.suggested_to || '',
        cc: '',
        subject: res.data.subject || '',
        body: res.data.body || '',
      });
      toast.success('Email composed by AI');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to compose email');
    } finally {
      setComposing(false);
    }
  };

  const handleSend = async () => {
    if (!emailForm.to) return toast.error('Recipient email required');
    if (!emailForm.subject) return toast.error('Subject required');
    setSending(true);
    try {
      await email.send(emailForm);
      toast.success('Email sent successfully!');
      setComposedEmail(null);
      setTranscript('');
      setEmailForm({ to: '', cc: '', subject: '', body: '' });
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to send email');
    } finally {
      setSending(false);
    }
  };

  if (!browserSupported) {
    return (
      <div className="max-w-2xl mx-auto">
        <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-6 text-center">
          <p className="text-yellow-800 font-medium">Voice recording requires Chrome or Edge</p>
          <p className="text-yellow-600 text-sm mt-1">You can still type your message below and use AI to compose it.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Voice Email</h1>
        <p className="text-gray-500 text-sm mt-1">
          Speak naturally — Claude AI will turn it into a professional email.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left column: context selectors + tips */}
        <div className="space-y-4">
          <div className="bg-white rounded-xl border border-gray-200 p-4">
            <h3 className="font-medium text-gray-900 text-sm mb-3">Context (optional)</h3>
            <div className="space-y-3">
              <div>
                <label className="label">Building</label>
                <select className="input" value={selectedBuilding} onChange={(e) => setSelectedBuilding(e.target.value)}>
                  <option value="">None</option>
                  {buildingList.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
                </select>
              </div>
              <div>
                <label className="label">Contractor</label>
                <select className="input" value={selectedContractor} onChange={(e) => setSelectedContractor(e.target.value)}>
                  <option value="">None</option>
                  {contractorList.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>
            </div>
          </div>

          <div className="bg-blue-50 rounded-xl border border-blue-100 p-4">
            <h3 className="text-sm font-medium text-blue-900 mb-2">Tips for best results</h3>
            <ul className="space-y-1.5">
              {RECORDING_TIPS.map((tip) => (
                <li key={tip} className="text-xs text-blue-700 flex items-start gap-1.5">
                  <span className="text-blue-400 mt-0.5">•</span>{tip}
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Right column: recorder + email */}
        <div className="lg:col-span-2 space-y-4">
          {/* Voice recorder */}
          <div className="bg-white rounded-xl border border-gray-200 p-5">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-medium text-gray-900">Voice Transcription</h3>
              {transcript && (
                <button onClick={() => { setTranscript(''); setComposedEmail(null); }}
                  className="text-xs text-gray-400 hover:text-gray-600">Clear</button>
              )}
            </div>

            {/* Record button */}
            <div className="flex justify-center mb-4">
              <button
                onClick={isRecording ? stopRecording : startRecording}
                className={`w-20 h-20 rounded-full flex items-center justify-center transition-all shadow-lg ${
                  isRecording
                    ? 'bg-red-500 hover:bg-red-600 animate-pulse'
                    : 'bg-blue-600 hover:bg-blue-700'
                }`}>
                {isRecording ? (
                  <svg className="w-8 h-8 text-white" fill="currentColor" viewBox="0 0 24 24">
                    <rect x="6" y="6" width="12" height="12" rx="1" />
                  </svg>
                ) : (
                  <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                      d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z" />
                  </svg>
                )}
              </button>
            </div>
            <p className="text-center text-xs text-gray-400 mb-4">
              {isRecording ? '🔴 Recording… click to stop' : 'Click to start recording'}
            </p>

            {/* Transcript box */}
            <textarea
              className="w-full border border-gray-200 rounded-lg p-3 text-sm min-h-32 resize-none outline-none focus:border-blue-400"
              placeholder="Your transcript will appear here, or type directly…"
              value={transcript}
              onChange={(e) => setTranscript(e.target.value)}
            />

            <button
              onClick={handleCompose}
              disabled={composing || !transcript.trim()}
              className="w-full mt-3 py-2.5 bg-purple-600 hover:bg-purple-700 text-white text-sm font-medium rounded-lg transition-colors disabled:opacity-50 flex items-center justify-center gap-2">
              {composing ? (
                <>
                  <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
                  </svg>
                  Composing with AI…
                </>
              ) : (
                <>
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                      d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                  </svg>
                  Compose with AI
                </>
              )}
            </button>
          </div>

          {/* Composed email form */}
          {composedEmail && (
            <div className="bg-white rounded-xl border border-gray-200 p-5">
              <h3 className="font-medium text-gray-900 mb-3 flex items-center gap-2">
                <svg className="w-4 h-4 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                    d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
                Composed Email
              </h3>
              <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2.5 text-xs text-amber-800 mb-4">
                <svg className="w-3.5 h-3.5 text-amber-500 shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span><strong>AI draft — review before sending.</strong> Check the recipient, subject, and body before clicking Send.</span>
              </div>
              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="label">To *</label>
                    <input type="email" className="input" value={emailForm.to}
                      onChange={(e) => setEmailForm({ ...emailForm, to: e.target.value })}
                      placeholder="contractor@example.com" />
                  </div>
                  <div>
                    <label className="label">CC</label>
                    <input type="email" className="input" value={emailForm.cc}
                      onChange={(e) => setEmailForm({ ...emailForm, cc: e.target.value })} />
                  </div>
                </div>
                <div>
                  <label className="label">Subject</label>
                  <input className="input" value={emailForm.subject}
                    onChange={(e) => setEmailForm({ ...emailForm, subject: e.target.value })} />
                </div>
                <div>
                  <label className="label">Body</label>
                  <textarea className="input resize-none" rows={8} value={emailForm.body}
                    onChange={(e) => setEmailForm({ ...emailForm, body: e.target.value })} />
                </div>
                <button onClick={handleSend} disabled={sending}
                  className="w-full py-2.5 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg disabled:opacity-50 flex items-center justify-center gap-2">
                  {sending ? 'Sending…' : (
                    <>
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                          d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                      </svg>
                      Send Email
                    </>
                  )}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      <style>{`
        .label { display: block; font-size: 0.75rem; font-weight: 500; color: #374151; margin-bottom: 0.25rem; }
        .input { width: 100%; border: 1px solid #d1d5db; border-radius: 0.5rem; padding: 0.5rem 0.75rem; font-size: 0.875rem; outline: none; }
        .input:focus { border-color: #3b82f6; box-shadow: 0 0 0 3px rgba(59,130,246,0.1); }
      `}</style>
    </div>
  );
}
